import React, {useEffect, useRef, useState} from 'react';
import {Tooltip, Typography, useMediaQuery} from '@mui/material';
import Box from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkerUtil from '../../../../../../utils/orders/WorkerUtil';
import ButtonCreate from '../../../../button/ButtonCreate';
import {
    formatJobCompletion,
    formatJobCount,
    formatJobNormOfTime,
} from './technicalMapFormatters';
import TechnicalMapJobWorkersAccordion from './TechnicalMapJobWorkersAccordion';
import {TECHNICAL_MAP_CARD_BUTTONS_STACK_MEDIA_QUERY, technicalMapCardButtonsStackMq, technicalMapMobileMq} from './technicalMapBreakpoints';
import {
    technicalMapCardFieldInlineLabelSx,
    technicalMapCardFieldInlineRowSx,
    technicalMapCardFieldLabelCellSx,
    technicalMapCardFieldLabelSx,
    technicalMapCardFieldRowSx,
    technicalMapCardFieldValueCellSx,
    technicalMapCardFieldValueSx,
    technicalMapCardSectionTitleSx,
    technicalMapGroupedCardSx,
    technicalMapGroupedFieldInlineRowSx,
    technicalMapGroupedFieldLabelSx,
    technicalMapGroupedFieldRowSx,
    technicalMapGroupedFieldValueSx,
} from './technicalMapCardStyles';

const JOB_FIELDS = [
    {label: 'Материал', getValue: (job) => job.materialName},
    {label: 'Описание', getValue: (job) => job.metricName},
    {
        label: 'Количество',
        getValue: (job) => `${formatJobCount(job.count)} ${job.unitName ?? ''}`.trim(),
    },
    {label: 'НВ/ч', getValue: (job) => formatJobNormOfTime(job.normOfTime)},
    {label: 'Дата выполнения', getValue: (job) => formatJobCompletion(job.completion)},
    {label: 'Отметки', getValue: (job) => WorkerUtil.convertWorkers(job.jobWorkers) || '—'},
];

const getCardTitle = (job) => job.typeJobName || 'Без типа';

const JobFieldRow = ({label, value, index, compact = false}) => {
    const displayValue = value ?? '—';

    return (
        <>
            <Box
                sx={{
                    ...(compact ? technicalMapGroupedFieldRowSx : technicalMapCardFieldRowSx),
                    paddingLeft: 0,
                    paddingRight: 0,
                    backgroundColor: compact ? 'white' : (index % 2 === 0 ? '#F5F6FA' : 'white'),
                }}
            >
                <Box sx={technicalMapCardFieldLabelCellSx}>
                    <Typography
                        noWrap
                        title={label}
                        sx={compact ? technicalMapGroupedFieldLabelSx : technicalMapCardFieldLabelSx}
                    >
                        {label}
                    </Typography>
                </Box>
                <Box sx={technicalMapCardFieldValueCellSx}>
                    <Typography
                        noWrap
                        title={displayValue}
                        sx={compact ? technicalMapGroupedFieldValueSx : technicalMapCardFieldValueSx}
                    >
                        {displayValue}
                    </Typography>
                </Box>
            </Box>
            <Typography
                component="div"
                sx={compact ? technicalMapGroupedFieldInlineRowSx : technicalMapCardFieldInlineRowSx}
            >
                <Box component="span" sx={technicalMapCardFieldInlineLabelSx}>
                    {label}
                    :
                </Box>
                {' '}
                {displayValue}
            </Typography>
        </>
    );
};

const TechnicalMapJobCard = ({
                                 job,
                                 isGrouped = false,
                                 expandAll,
                                 expandAllRevision,
                                 setData,
                                 handleModalAlert,
                                 isSettingsRole,
                                 isActionActive,
                                 onExecuteJob,
                                 onCancelExecuteJob,
                                 onDeleteJob,
                                 workersExpanded = false,
                                 toggleWorkersExpanded,
                                 setWorkersExpanded,
                             }) => {
    const [cardExpanded, setCardExpanded] = useState(true);
    const workersToggleGuardRef = useRef(0);
    const areCardButtonsStacked = useMediaQuery(TECHNICAL_MAP_CARD_BUTTONS_STACK_MEDIA_QUERY, {noSsr: true});
    const jobIdKey = String(job.id);
    const canEditWorkers = isActionActive('edit', job);
    const canExecute = isActionActive('execute', job);
    const canCancelExecute = isActionActive('cancelExecute', job);
    const canDelete = isActionActive('delete', job);

    useEffect(() => {
        if (!canEditWorkers && workersExpanded) {
            setWorkersExpanded?.(jobIdKey, false);
        }
    }, [canEditWorkers, jobIdKey, setWorkersExpanded, workersExpanded]);

    useEffect(() => {
        if (!isGrouped && expandAllRevision > 0) {
            setCardExpanded(expandAll);
        }
    }, [expandAllRevision, expandAll, isGrouped]);

    const handleWorkersClick = (event) => {
        event.stopPropagation();
        if (!canEditWorkers) {
            return;
        }
        const now = Date.now();
        if (now - workersToggleGuardRef.current < 350) {
            return;
        }
        workersToggleGuardRef.current = now;
        toggleWorkersExpanded?.(jobIdKey);
    };

    const jobFields = JOB_FIELDS;
    const cardTitle = getCardTitle(job);
    const fieldsBoxSx = {
        px: isGrouped ? 1.5 : 2,
        pt: 0.5,
        [technicalMapMobileMq]: {
            px: 0.5,
            pt: 0.25,
        },
    };
    const actionButtonHeight = isGrouped ? 38 : 44;
    const actionButtonsGridSx = {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: isGrouped ? 0.75 : 1,
        px: isGrouped ? 1.5 : 2,
        pb: isGrouped ? 1.5 : 2,
        pt: 0.25,
        [technicalMapMobileMq]: {
            gap: isGrouped ? 0.5 : 0.75,
            px: 0.5,
            pb: isGrouped ? 1 : 1.25,
            pt: 0,
        },
        [technicalMapCardButtonsStackMq]: {
            gridTemplateColumns: '1fr',
        },
    };
    const cardActionButtonSx = {
        width: '100%',
        minWidth: 0,
        justifyContent: 'center',
        whiteSpace: 'normal',
        textAlign: 'center',
        lineHeight: 1.2,
        fontSize: '16px',
        px: 1,
        textTransform: 'none',
        fontFamily: (theme) => theme.typography.fontFamily,
    };
    const editWorkersButtonSx = {
        ...cardActionButtonSx,
        height: 'auto',
        minHeight: actionButtonHeight,
        py: 0.75,
        position: 'relative',
        pr: 3.5,
        display: 'flex',
        alignItems: 'center',
        borderRadius: '4px',
        color: (theme) => theme.palette.text.main,
        borderColor: (theme) => theme.palette.primary.main,
        backgroundColor: 'white',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        '&:hover': {
            backgroundColor: 'white',
            borderColor: (theme) => theme.palette.primary.main,
        },
    };
    const workersButtonArrowSx = {
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        lineHeight: 1,
        pointerEvents: 'none',
    };
    const executeButtonSx = {
        ...cardActionButtonSx,
        backgroundColor: (theme) => theme.palette.primary.main,
        color: 'white',
        borderColor: (theme) => theme.palette.primary.main,
        '&:hover': {
            backgroundColor: (theme) => theme.palette.primary.main,
            borderColor: (theme) => theme.palette.primary.main,
        },
        '&.Mui-disabled': {
            opacity: 1,
            backgroundColor: (theme) => theme.palette.primary.light50,
            color: (theme) => theme.palette.action.disabled,
            borderColor: (theme) => theme.palette.primary.light50,
        },
    };
    const deleteButtonSx = {
        ...cardActionButtonSx,
        borderColor: (theme) => theme.palette.error.main,
        color: (theme) => theme.palette.error.main,
    };

    const renderActionButton = (disabled, button) => {
        const content = (
            <Box sx={{minWidth: 0, width: '100%'}}>
                {button}
            </Box>
        );
        if (!disabled) {
            return content;
        }
        return (
            <Tooltip arrow placement="top" title="заблокировано" disableTouchListener>
                <span style={{display: 'block', width: '100%'}}>
                    {content}
                </span>
            </Tooltip>
        );
    };

    const workersPanelBelowSx = {
        px: isGrouped ? 1.5 : 2,
        pb: isGrouped ? 1.25 : 1.5,
        pt: 0.25,
        [technicalMapMobileMq]: {
            px: 0.5,
        },
    };
    const workersPanelStackedSx = {
        pt: 0.5,
    };

    const workersButtonLabel = (
        <>
            <Box
                component="span"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'center',
                    lineHeight: 1.2,
                    pointerEvents: 'none',
                }}
            >
                Изменить исполнителей
            </Box>
            <Box component="span" sx={workersButtonArrowSx}>
                {workersExpanded ? '▲' : '▼'}
            </Box>
        </>
    );

    const workersPanel = workersExpanded ? (
        <Box sx={areCardButtonsStacked ? workersPanelStackedSx : workersPanelBelowSx}>
            <TechnicalMapJobWorkersAccordion
                jobId={job.id}
                expanded={workersExpanded}
                disabled={!canEditWorkers}
                setData={setData}
                handleModalAlert={handleModalAlert}
            />
        </Box>
    ) : null;

    const workersButton = renderActionButton(
        !canEditWorkers,
        <ButtonCreate
            text={workersButtonLabel}
            icon={false}
            width="100%"
            height={actionButtonHeight}
            disabled={!canEditWorkers}
            onClick={handleWorkersClick}
            sx={editWorkersButtonSx}
        />,
    );

    const cardActions = areCardButtonsStacked ? (
        <Box sx={actionButtonsGridSx}>
            <Box>
                {workersButton}
                {workersPanel}
            </Box>
            {renderActionButton(
                !canExecute,
                <ButtonCreate
                    text="Выполнить работу"
                    icon={false}
                    width="100%"
                    height={actionButtonHeight}
                    disabled={!canExecute}
                    onClick={() => onExecuteJob(job)}
                    sx={executeButtonSx}
                />,
            )}
            {isSettingsRole && renderActionButton(
                !canCancelExecute,
                <ButtonCreate
                    text="Отменить выполнение"
                    icon={false}
                    width="100%"
                    height={actionButtonHeight}
                    disabled={!canCancelExecute}
                    onClick={() => onCancelExecuteJob(job)}
                    sx={cardActionButtonSx}
                />,
            )}
            {isSettingsRole && renderActionButton(
                !canDelete,
                <ButtonCreate
                    text="Удалить"
                    icon={false}
                    width="100%"
                    height={actionButtonHeight}
                    disabled={!canDelete}
                    onClick={() => onDeleteJob(job)}
                    sx={deleteButtonSx}
                />,
            )}
        </Box>
    ) : (
        <Box sx={actionButtonsGridSx}>
            {workersButton}
            {renderActionButton(
                !canExecute,
                <ButtonCreate
                    text="Выполнить работу"
                    icon={false}
                    width="100%"
                    height={actionButtonHeight}
                    disabled={!canExecute}
                    onClick={() => onExecuteJob(job)}
                    sx={executeButtonSx}
                />,
            )}
            {isSettingsRole && renderActionButton(
                !canCancelExecute,
                <ButtonCreate
                    text="Отменить выполнение"
                    icon={false}
                    width="100%"
                    height={actionButtonHeight}
                    disabled={!canCancelExecute}
                    onClick={() => onCancelExecuteJob(job)}
                    sx={cardActionButtonSx}
                />,
            )}
            {isSettingsRole && renderActionButton(
                !canDelete,
                <ButtonCreate
                    text="Удалить"
                    icon={false}
                    width="100%"
                    height={actionButtonHeight}
                    disabled={!canDelete}
                    onClick={() => onDeleteJob(job)}
                    sx={deleteButtonSx}
                />,
            )}
        </Box>
    );

    const cardFields = (
        <Box sx={fieldsBoxSx}>
            {jobFields.map((field, index) => (
                <JobFieldRow
                    key={field.label}
                    label={field.label}
                    value={field.getValue(job)}
                    index={index}
                    compact={isGrouped}
                />
            ))}
        </Box>
    );

    const cardBody = (
        <Box>
            {cardFields}
            {cardActions}
            {!areCardButtonsStacked && workersPanel}
        </Box>
    );

    if (isGrouped) {
        return (
            <Box sx={technicalMapGroupedCardSx}>
                {cardBody}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                border: '1px solid #EAEBEA',
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            }}
        >
            <Box
                onClick={() => setCardExpanded((prev) => !prev)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: '12px',
                    py: '10px',
                    backgroundColor: '#F5F6FA',
                    borderBottom: cardExpanded ? '1px solid #EAEBEA' : 'none',
                    cursor: 'pointer',
                    userSelect: 'none',
                    [technicalMapMobileMq]: {
                        px: '4px',
                        py: '6px',
                        gap: 0.5,
                    },
                }}
            >
                <Typography
                    sx={{
                        ...technicalMapCardSectionTitleSx,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {cardTitle}
                </Typography>
                <ExpandMoreIcon
                    sx={{
                        flexShrink: 0,
                        fontSize: 28,
                        color: 'text.secondary',
                        transform: cardExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                    }}
                />
            </Box>

            {cardExpanded && cardBody}
        </Box>
    );
};

export default TechnicalMapJobCard;
